import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field } from "./Customers";
import { Plus, Play, Check, X, Wrench, MapPin, User, AlertTriangle } from "lucide-react";

const PRIORITY = {
  low: { color: "#495057", label: "low" },
  normal: { color: "#0033A0", label: "normal" },
  high: { color: "#F5B700", label: "high" },
  critical: { color: "#D90429", label: "critical" },
};
const STATUS = {
  open: { bg: "bg-white border-[#0A0A0A] text-[#0A0A0A]", label: "open" },
  in_progress: { bg: "bg-[#F5B700] border-[#F5B700] text-[#0A0A0A]", label: "in progress" },
  completed: { bg: "bg-[#0B7A75] border-[#0B7A75] text-white", label: "completed" },
  cancelled: { bg: "bg-[#495057] border-[#495057] text-white", label: "cancelled" },
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/jobs").then(({ data }) => setJobs(data));
  useEffect(() => { load(); }, []);
  const filtered = filter === "all" ? jobs : jobs.filter((j) => j.status === filter);

  return (
    <div className="p-6 lg:p-12 max-w-[1400px]" data-testid="jobs-page">
      <PageHeader
        title="Engineering · Field service"
        subtitle="Job orders for installations, maintenance & repairs. Mobile-friendly cards — technicians can work from a phone."
        action={
          <button onClick={() => setOpen(true)} data-testid="new-job-btn"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm rounded-sm">
            <Plus size={14} /> New job
          </button>
        }
      />

      <div className="mt-6 flex flex-wrap gap-2">
        {["all", "open", "in_progress", "completed", "cancelled"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`jobs-filter-${s}`}
            className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border ${filter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] text-[#495057]"}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((j, i) => (
          <button key={j.id} onClick={() => setView(j)} data-testid={`job-card-${i}`}
            className="text-left bg-white border border-[#DEE2E6] hover:border-[#FF4F00] transition-colors p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-xs text-[#495057]">{j.number}</div>
                <div className="font-heading font-semibold tracking-tight mt-1">{j.equipment_label}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 ${STATUS[j.status].bg}`}>{STATUS[j.status].label}</span>
            </div>
            <div className="text-sm text-[#495057] line-clamp-2 mb-4">{j.issue_summary}</div>
            <div className="flex flex-wrap gap-3 text-xs text-[#495057]">
              <span className="inline-flex items-center gap-1"><User size={11} /> {j.customer_name}</span>
              {j.technician_name && <span className="inline-flex items-center gap-1"><Wrench size={11} /> {j.technician_name}</span>}
              <span className="inline-flex items-center gap-1"><AlertTriangle size={11} style={{ color: PRIORITY[j.priority]?.color }} /> {PRIORITY[j.priority]?.label}</span>
            </div>
          </button>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-[#495057] p-8 border border-dashed border-[#DEE2E6]">No jobs.</div>}
      </div>

      {open && <NewJobForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {view && <JobDetail job={view} onClose={() => setView(null)} onChanged={() => { setView(null); load(); }} />}
    </div>
  );
}

function NewJobForm({ onClose, onSaved }) {
  const [custs, setCusts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [f, setF] = useState({ customer_id: "", equipment_label: "", issue_summary: "", priority: "normal", technician_id: "", site_address: "" });
  const [err, setErr] = useState("");
  useEffect(() => {
    Promise.all([api.get("/customers"), api.get("/jobs/technicians")]).then(([c, t]) => { setCusts(c.data); setTechs(t.data); });
  }, []);
  const save = async (e) => { e.preventDefault();
    try { await api.post("/jobs", { ...f, technician_id: f.technician_id || null }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="New job order" onClose={onClose} testid="new-job-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="new-job-form">
        <label className="col-span-2 block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Customer</span>
          <select required value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="job-customer"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {custs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <div className="col-span-2"><Field label="Equipment / asset" required value={f.equipment_label} onChange={(v) => setF({ ...f, equipment_label: v })} testid="job-equipment" /></div>
        <div className="col-span-2"><Field label="Issue summary" required value={f.issue_summary} onChange={(v) => setF({ ...f, issue_summary: v })} testid="job-issue" /></div>
        <div className="col-span-2"><Field label="Site address" value={f.site_address} onChange={(v) => setF({ ...f, site_address: v })} testid="job-site" /></div>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Priority</span>
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="job-priority"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            {Object.keys(PRIORITY).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Assign technician (optional)</span>
          <select value={f.technician_id} onChange={(e) => setF({ ...f, technician_id: e.target.value })} data-testid="job-tech"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— unassigned —</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.specialty}</option>)}
          </select>
        </label>
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="job-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

function JobDetail({ job: initial, onClose, onChanged }) {
  const [job, setJob] = useState(initial);
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState(job.parts_used || []);
  const [notes, setNotes] = useState(job.completion_notes || "");
  const [signoff, setSignoff] = useState(!!job.customer_signoff);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/items").then(({ data }) => setItems(data)); }, []);

  const persist = async () => {
    await api.patch(`/jobs/${job.id}`, { parts_used: parts, completion_notes: notes, customer_signoff: signoff });
    const { data } = await api.get(`/jobs/${job.id}`);
    setJob(data); onChanged?.();
  };
  const action = async (path) => {
    setBusy(true); setErr("");
    try { await persist(); await api.post(`/jobs/${job.id}/${path}`); const { data } = await api.get(`/jobs/${job.id}`); setJob(data); onChanged?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const addPart = () => setParts((p) => [...p, { item_id: "", qty: 1 }]);
  const updPart = (i, patch) => setParts((p) => p.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  return (
    <Modal title={`${job.number} · ${job.equipment_label}`} onClose={onClose} testid="job-detail-modal" wide>
      <div className="space-y-5" data-testid="job-detail">
        <div className="flex flex-wrap gap-3 items-center">
          <span className={`inline-block text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 ${STATUS[job.status].bg}`}>{STATUS[job.status].label}</span>
          <span className="text-xs text-[#495057]"><User size={11} className="inline mr-1" /> {job.customer_name}</span>
          {job.technician_name && <span className="text-xs text-[#495057]"><Wrench size={11} className="inline mr-1" /> {job.technician_name}</span>}
          {job.site_address && <span className="text-xs text-[#495057]"><MapPin size={11} className="inline mr-1" /> {job.site_address}</span>}
        </div>

        <div className="border border-[#DEE2E6] bg-[#F8F9FA] p-4 text-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mb-1">Issue</div>
          <div>{job.issue_summary}</div>
        </div>

        {job.status !== "completed" && job.status !== "cancelled" && (
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Parts used (decrements stock on completion)</div>
            <div className="border border-[#DEE2E6]">
              {parts.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_30px] gap-2 px-3 py-2 border-b border-[#DEE2E6] items-center" data-testid={`job-part-${i}`}>
                  <select value={p.item_id} onChange={(e) => {
                    const it = items.find((x) => x.id === e.target.value);
                    updPart(i, { item_id: e.target.value, sku: it?.sku, name: it?.name });
                  }} className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm">
                    <option value="">— pick part —</option>
                    {items.map((x) => <option key={x.id} value={x.id}>{x.sku} · {x.name} (stock {x.stock_on_hand})</option>)}
                  </select>
                  <input type="number" min="0.01" step="0.01" value={p.qty} onChange={(e) => updPart(i, { qty: parseFloat(e.target.value) })}
                    className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm text-right" />
                  <button onClick={() => setParts((x) => x.filter((_, idx) => idx !== i))} className="text-[#D90429]"><X size={14} /></button>
                </div>
              ))}
              <button onClick={addPart} data-testid="job-add-part" className="w-full px-3 py-2 text-xs text-[#0033A0] hover:bg-[#F8F9FA] text-left">+ Add part</button>
            </div>

            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Completion notes…"
              data-testid="job-notes"
              className="w-full mt-3 border border-[#DEE2E6] px-3 py-2 text-sm" />

            <label className="mt-3 flex items-center gap-2 text-sm" data-testid="job-signoff-label">
              <input type="checkbox" checked={signoff} onChange={(e) => setSignoff(e.target.checked)} data-testid="job-signoff" />
              Customer sign-off received
            </label>
          </div>
        )}

        {err && <div className="text-sm text-[#D90429] border border-[#D90429] bg-[#D90429]/5 px-3 py-2">{err}</div>}

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-[#DEE2E6]">
          {job.status === "open" && (
            <>
              <button onClick={() => action("cancel")} disabled={busy} data-testid="job-cancel-btn" className="px-4 py-2 text-sm border border-[#D90429] text-[#D90429]">Cancel</button>
              <button onClick={() => action("start")} disabled={busy} data-testid="job-start-btn" className="px-4 py-2 text-sm bg-[#F5B700] text-[#0A0A0A] inline-flex items-center gap-1.5"><Play size={14} /> Start work</button>
            </>
          )}
          {job.status === "in_progress" && (
            <>
              <button onClick={persist} disabled={busy} data-testid="job-save-btn" className="px-4 py-2 text-sm border border-[#0A0A0A]">Save progress</button>
              <button onClick={() => action("complete")} disabled={busy} data-testid="job-complete-btn" className="px-4 py-2 text-sm bg-[#0B7A75] text-white inline-flex items-center gap-1.5"><Check size={14} /> Complete job</button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
